import { z } from "zod";
import { AgentProposalStateSchema, AgentProviderInvocationSchema } from "./agent-proposals.js";
import { ConversationRuleSchema } from "./conversation-scope.js";
import { OpsAssistantWorkspaceReferenceSchema } from "./ops-assistant-context.js";
import { OpsAssistantArtifactSchema, OpsAssistantReferenceSchema, SafeExternalHttpUrlSchema } from "./ops-assistant-content.js";
import { OpsImageJobProjectionSchema } from "./ops-image.js";
import { OpsSha256Schema, opsCanonicalJson, opsSha256, opsSha256Utf8 } from "./ops-release-identity.js";
import { ScheduledSendSchema } from "./scheduled-send.js";
import { WorkKindSchema, WorkStatusSchema, validateWorkSchedule } from "./work-items.js";
import { STANDARD_AGENT_MESSAGE_DRAFT_MAX_LENGTH, isUnicodeScalarTextWithinLength } from "./proposal-message-draft.js";
export const OPS_PUBLIC_TURN_SCHEMA = "ops.public-turn.v1";
export const OPS_PUBLIC_TURN_RESULT_SCHEMA = "ops.turn-result.v1";
export const OPS_PUBLIC_PROOF_SCHEMA = "ops.live-core-proof-summary.v1";
export const OPS_EVIDENCE_ANCHOR_SCHEMA = "ops.evidence-anchor.v1";
const MAX_PUBLIC_EVIDENCE_SOURCES = 320;
const PublicIdentifierSchema = z.string().min(1).max(160).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u);
const ActiveWorkStatusSchema = WorkStatusSchema.exclude(["ARCHIVED"]);
const OperationalMessageDraftSchema = z.string().trim().min(1)
    .max(STANDARD_AGENT_MESSAGE_DRAFT_MAX_LENGTH * 2).refine((value) => isUnicodeScalarTextWithinLength(value, STANDARD_AGENT_MESSAGE_DRAFT_MAX_LENGTH), { message: "Operational message drafts must contain at most 300 valid Unicode characters." });
const CreateWorkCommandSchema = z.object({
    action: z.literal("CREATE"),
    title: z.string().trim().min(1).max(240),
    assigneeId: z.uuid().nullable().optional(),
    dueAt: z.iso.datetime().nullable().optional(),
    workKind: WorkKindSchema.optional(),
    scheduledStartAt: z.iso.datetime().nullable().optional(),
    scheduledEndAt: z.iso.datetime().nullable().optional(),
    scheduledAllDay: z.boolean().optional()
}).strict().superRefine((command, context) => {
    const problem = validateWorkSchedule(command);
    if (problem)
        context.addIssue({ code: "custom", path: ["scheduledStartAt"], message: problem });
});
export const OpsAssistantPublicMutationCommandSchema = z.discriminatedUnion("action", [
    CreateWorkCommandSchema,
    z.object({ action: z.literal("STATUS"), workItemId: z.uuid(), expectedVersion: z.int().positive(), status: ActiveWorkStatusSchema }).strict(),
    z.object({ action: z.literal("ASSIGNMENT"), workItemId: z.uuid(), expectedVersion: z.int().positive(), assigneeId: z.uuid().nullable() }).strict(),
    z.object({ action: z.literal("DEADLINE"), workItemId: z.uuid(), expectedVersion: z.int().positive(), dueAt: z.iso.datetime().nullable() }).strict(),
    z.object({
        action: z.literal("TASK_RANGE"),
        workItemId: z.uuid(),
        expectedVersion: z.int().positive(),
        taskStartAt: z.iso.datetime().nullable(),
        taskEndAt: z.iso.datetime().nullable()
    }).strict().superRefine((command, context) => {
        const problem = validateWorkSchedule({ workKind: "TASK", ...command });
        if (problem)
            context.addIssue({ code: "custom", path: ["taskStartAt"], message: problem });
    }),
    z.object({
        action: z.literal("WORK_SCHEDULE"),
        workItemId: z.uuid(),
        expectedVersion: z.int().positive(),
        workKind: WorkKindSchema,
        scheduledStartAt: z.iso.datetime().nullable(),
        scheduledEndAt: z.iso.datetime().nullable(),
        scheduledAllDay: z.boolean()
    }).strict().superRefine((command, context) => {
        const problem = validateWorkSchedule(command);
        if (problem)
            context.addIssue({ code: "custom", path: ["scheduledStartAt"], message: problem });
    }),
    z.object({ action: z.literal("NOTE"), workItemId: z.uuid(), expectedVersion: z.int().positive(), body: z.string().trim().min(1).max(10_000) }).strict(),
    z.object({
        action: z.literal("CONFIGURE_WHATSAPP_ROUTE"),
        conversationId: z.uuid(),
        expectedPolicyVersion: z.int().nonnegative(),
        recipientLabel: z.string().trim().min(1).max(200),
        conversationRule: ConversationRuleSchema,
        clientId: z.uuid().nullable()
    }).strict(),
    z.object({
        action: z.literal("DRAFT_WHATSAPP"),
        conversationId: z.uuid(),
        expectedPolicyId: z.uuid(),
        expectedPolicyVersion: z.int().positive(),
        expectedNewestMessageAt: z.iso.datetime().nullable(),
        recipientLabel: z.string().trim().min(1).max(200),
        messageDraft: OperationalMessageDraftSchema
    }).strict(),
    z.object({
        action: z.literal("SCHEDULE_WHATSAPP"),
        workItemId: z.uuid(),
        expectedWorkItemVersion: z.int().positive(),
        conversationId: z.uuid(),
        expectedPolicyId: z.uuid(),
        expectedPolicyVersion: z.int().positive(),
        expectedNewestMessageAt: z.iso.datetime().nullable(),
        recipientLabel: z.string().trim().min(1).max(200),
        messageDraft: OperationalMessageDraftSchema,
        schedule: ScheduledSendSchema
    }).strict()
]);
const OpsEvidenceContactSchema = z.object({
    phoneE164: z.string().regex(/^\+[1-9][0-9]{7,14}$/u),
    actions: z.array(z.enum(["CALL", "WHATSAPP"])).min(1).max(2)
}).strict().superRefine((contact, context) => {
    if (new Set(contact.actions).size !== contact.actions.length) {
        context.addIssue({
            code: "custom",
            path: ["actions"],
            message: "A proof contact action may appear only once."
        });
    }
});
export const OpsEvidenceSourceChannelSchema = z.enum([
    "WHATSAPP",
    "MEETING",
    "GOOGLE_MEET",
    "OPS_UPLOAD"
]);
export const OpsReactionProofSchema = z.object({
    text: z.string().nullable(),
    target: z.union([
        z.object({ kind: z.literal("AVAILABLE"), sourceEventId: z.uuid() }).strict(),
        z.object({ kind: z.literal("UNAVAILABLE") }).strict()
    ])
}).strict();
/** Provider-authored deletion status attached to the existing target proof.
 * This annotation is not message body and never identifies the deleting actor. */
export const OpsSourceDeletionSchema = z.object({
    kind: z.literal("REVOKE"),
    sourceEventId: z.uuid()
}).strict();
// Archive members keep their parent source UUID, but a parent ZIP is not a
// proof occurrence by itself. This immutable sidecar distinguishes two equal
// member bodies (and a later re-packed parent) without changing ordinary proof
// bytes when it is absent.
export const OpsArchiveMemberIdentitySchema = z.object({
    parentOriginalSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    parentOriginalBytes: z.int().positive(),
    memberIndex: z.int().nonnegative(),
    path: z.string().min(1).max(4_096),
    memberSha256: z.string().regex(/^[a-f0-9]{64}$/u)
}).strict();
const EvidenceSourceSchema = z.object({
    sourceEventId: z.uuid(),
    conversationLabel: z.string().min(1).max(240),
    senderLabel: z.string().min(1).max(240),
    occurredAt: z.iso.datetime(),
    kind: z.enum(["TEXT", "VOICE_TRANSCRIPT", "FILE"]),
    excerpt: z.string().max(8_000),
    reaction: OpsReactionProofSchema.optional(),
    deletions: z.array(OpsSourceDeletionSchema).min(1).max(8).optional(),
    // Optional only for already-persisted v1 turns. Every new producer carries
    // the immutable adapter channel so VOICE_TRANSCRIPT remains presentation,
    // never authority to invent WhatsApp navigation.
    channel: OpsEvidenceSourceChannelSchema.optional(),
    // Source navigation is an explicit server capability. The renderer must not
    // infer WhatsApp ownership from TEXT/VOICE_TRANSCRIPT presentation kinds.
    sourceActions: z.tuple([z.literal("OPEN_MESSAGE")]).optional(),
    // Optional contact controls are a server-authorized projection of one exact
    // verified direct source. They are presentation capabilities, never proof
    // identity or authority to send without the ordinary approval path.
    contact: OpsEvidenceContactSchema.optional(),
    archiveMember: OpsArchiveMemberIdentitySchema.optional()
}).strict();
export const OpsVisualProofSchema = z.object({
    kind: z.enum(["PDF_PAGE", "IMAGE"]),
    originalSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    imageSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    derivationVersion: z.string().min(1).max(120),
    pageNumber: z.int().positive().nullable(),
    region: z.object({
        left: z.number().finite(), bottom: z.number().finite(),
        right: z.number().finite(), top: z.number().finite()
    }).strict().refine((region) => region.left < region.right && region.bottom < region.top, { message: "Visual proof region must have ordered page coordinates." }).nullable(),
    archiveMember: OpsArchiveMemberIdentitySchema.optional()
}).strict().superRefine((visual, context) => {
    if ((visual.kind === "PDF_PAGE" && visual.pageNumber === null)
        || (visual.kind === "IMAGE" && (visual.pageNumber !== null || visual.region !== null))) {
        context.addIssue({ code: "custom", message: "Only PDF proof has a page number and page-coordinate region." });
    }
    if (visual.archiveMember && visual.archiveMember.memberSha256 !== visual.originalSha256) {
        context.addIssue({ code: "custom", path: ["archiveMember"], message: "Archive visual proof must bind its exact member bytes." });
    }
});
export function opsVisualProofKey(visual) {
    return opsSha256(OpsVisualProofSchema.parse(visual));
}
// WHY [BOL-OPS-CLAIM-SPAN-IDENTITY-001]: highlight text plus indexOf silently
// reassigned a claim when the same words occurred twice. Preserve the readable
// words and hash their exact half-open UTF-16 range into the public result.
// Falsifier: two valid occurrences remain indistinguishable, or the declared
// range selects any text other than the declared highlight.
const OpsPublicTextClaimEvidenceSourceSchema = EvidenceSourceSchema.extend({
    visual: z.never().optional(),
    reaction: z.never().optional(),
    highlight: z.string().min(1).max(8_000),
    // JavaScript/DOM string offsets are UTF-16 code-unit boundaries. Keeping the
    // exact half-open span in the canonical hashed result distinguishes repeated
    // source words without changing the human-readable highlight field.
    highlightStart: z.int().nonnegative().max(7_999),
    highlightEnd: z.int().positive().max(8_000)
}).strict().superRefine((source, context) => {
    if (source.kind === "FILE" && source.sourceActions !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["sourceActions"],
            message: "File proof uses its authenticated file action, never WhatsApp navigation."
        });
    }
    if (source.highlightEnd <= source.highlightStart) {
        context.addIssue({
            code: "custom",
            path: ["highlightEnd"],
            message: "Claim evidence highlight end must follow its start."
        });
        return;
    }
    if (source.highlightEnd > source.excerpt.length
        || source.excerpt.slice(source.highlightStart, source.highlightEnd) !== source.highlight) {
        context.addIssue({
            code: "custom",
            path: ["highlightStart"],
            message: "Claim evidence highlight span must select the exact contiguous highlight text in its excerpt."
        });
    }
});
// WHY [BOL-OPS-VISUAL-PROOF-IDENTITY-001]: exact text matching can authenticate
// an inspection caption without authenticating the original pixels. Visual
// occurrences instead bind the retained original and the exact model-visible
// page/image. Do not manufacture quotation offsets or reclassify WhatsApp as
// OPS_UPLOAD. Missing visual remains the byte-identical legacy text branch.
export const OpsPublicClaimEvidenceSourceSchema = z.union([
    OpsPublicTextClaimEvidenceSourceSchema,
    EvidenceSourceSchema.extend({
        visual: OpsVisualProofSchema,
        reaction: z.never().optional(),
        highlight: z.never().optional(),
        highlightStart: z.never().optional(),
        highlightEnd: z.never().optional()
    }).strict().superRefine((source, context) => {
        if (source.kind === "FILE" && source.sourceActions !== undefined) {
            context.addIssue({ code: "custom", path: ["sourceActions"], message: "File proof cannot acquire WhatsApp navigation." });
        }
    }),
    EvidenceSourceSchema.extend({
        reaction: OpsReactionProofSchema,
        visual: z.never().optional(),
        highlight: z.never().optional(),
        highlightStart: z.never().optional(),
        highlightEnd: z.never().optional()
    }).strict().superRefine((source, context) => {
        if (source.channel !== "WHATSAPP" || source.sourceActions?.[0] !== "OPEN_MESSAGE") {
            context.addIssue({ code: "custom", path: ["reaction"],
                message: "Reaction proof must retain authorized WhatsApp source navigation." });
        }
    })
]);
export const OpsEvidenceAnchorSchema = z.object({
    schema: z.literal(OPS_EVIDENCE_ANCHOR_SCHEMA),
    sourceEventId: z.uuid(),
    excerptHash: OpsSha256Schema,
    excerptLength: z.int().positive().max(8_000),
    highlightStart: z.int().nonnegative().max(7_999),
    highlightEnd: z.int().positive().max(8_000)
}).strict().superRefine((anchor, context) => {
    if (anchor.highlightEnd <= anchor.highlightStart || anchor.highlightEnd > anchor.excerptLength) {
        context.addIssue({
            code: "custom",
            path: ["highlightEnd"],
            message: "Evidence anchor span must be inside its exact source excerpt."
        });
    }
});
export const OpsAnswerClaimSpanSchema = z.object({
    text: z.string().min(1).max(2_000).refine((text) => text === text.trim(), { message: "Answer claim span text must not contain ornamental edge whitespace." }),
    start: z.int().nonnegative().max(19_999),
    end: z.int().positive().max(20_000)
}).strict().superRefine((span, context) => {
    if (span.end <= span.start || span.end - span.start !== span.text.length) {
        context.addIssue({
            code: "custom",
            path: ["end"],
            message: "Answer claim span must exactly match its UTF-16 text length."
        });
    }
});
const OPS_ANSWER_CLAIM_SEPARATOR = /^[\s*•-]*$/u;
/**
 * Bind model-authored claim wording to the unchanged visible answer. Code owns
 * only exact UTF-16 occurrence identity and separator validation; it neither
 * selects claims nor rewrites their meaning.
 */
export function bindOpsOrderedAnswerClaimSpans(answer, claimTexts) {
    let cursor = 0;
    const spans = [];
    for (const text of claimTexts) {
        const start = answer.indexOf(text, cursor);
        if (start < cursor || !OPS_ANSWER_CLAIM_SEPARATOR.test(answer.slice(cursor, start))) {
            return null;
        }
        const parsed = OpsAnswerClaimSpanSchema.safeParse({
            text,
            start,
            end: start + text.length
        });
        if (!parsed.success)
            return null;
        spans.push(parsed.data);
        cursor = parsed.data.end;
    }
    return OPS_ANSWER_CLAIM_SEPARATOR.test(answer.slice(cursor)) ? spans : null;
}
export const OpsPublicEvidenceClaimSchema = z.object({
    kind: z.enum(["FACT", "INFERENCE", "UNRESOLVED", "CONFLICT", "SOURCE_LINKED"]),
    subject: z.string().max(240),
    text: z.string().min(1).max(2_000),
    answerSpan: OpsAnswerClaimSpanSchema.optional(),
    evidenceSources: z.array(OpsPublicClaimEvidenceSourceSchema).max(MAX_PUBLIC_EVIDENCE_SOURCES),
    workItemIds: z.array(z.uuid()).max(4)
}).strict().superRefine((claim, context) => {
    if (claim.kind !== "SOURCE_LINKED" && claim.subject.trim().length === 0) {
        context.addIssue({ code: "custom", path: ["subject"], message: "A public claim subject cannot be blank." });
    }
    if (claim.text.trim().length === 0) {
        context.addIssue({ code: "custom", path: ["text"], message: "A public claim text cannot be blank." });
    }
    if (claim.answerSpan && claim.answerSpan.text !== claim.text) {
        context.addIssue({
            code: "custom",
            path: ["answerSpan", "text"],
            message: "A bound answer span must carry the exact public claim wording."
        });
    }
    if (claim.evidenceSources.length + claim.workItemIds.length === 0) {
        context.addIssue({
            code: "custom",
            message: "Every public evidence claim requires a directly bound source or Work item."
        });
    }
    claim.evidenceSources.forEach((source, sourceIndex) => {
        if (source.deletions && source.channel !== "WHATSAPP") {
            context.addIssue({
                code: "custom",
                path: ["evidenceSources", sourceIndex, "deletions"],
                message: "A deletion annotation belongs only to an existing WhatsApp source proof."
            });
        }
        if (source.deletions
            && (new Set(source.deletions.map((deletion) => deletion.sourceEventId)).size
                !== source.deletions.length
                || source.deletions.some((deletion) => deletion.sourceEventId === source.sourceEventId))) {
            context.addIssue({
                code: "custom",
                path: ["evidenceSources", sourceIndex, "deletions"],
                message: "Deletion annotations must retain distinct provider event identities."
            });
        }
    });
    // Direct-primary SOURCE_LINKED proof is occurrence-addressed and can require
    // more than the verifier's legacy eight proof occurrences for one compound
    // claim. Keep that transport inside the existing response-wide 320-source
    // envelope while classified public claims retain eight and upstream native,
    // verifier, and Work contracts retain their independent bounds. This is
    // capacity, not semantic proof: exact source bytes, ranges, duplicates and
    // entailment remain independently owned.
    if (claim.kind !== "SOURCE_LINKED" && claim.evidenceSources.length > 8) {
        context.addIssue({
            code: "custom",
            path: ["evidenceSources"],
            message: "A classified public claim may bind at most eight evidence sources."
        });
    }
    // WHY: the accepted Sol answer selected two disjoint passages in the same
    // message. Source-ID deduplication erased one or rejected the entire answer.
    // Preserve exact occurrences; SOURCE_LINKED asserts linkage, not fact truth.
    const proofKeys = claim.evidenceSources.map((source) => source.visual
        ? `${source.sourceEventId}:${opsVisualProofKey(source.visual)}`
        : source.reaction
            ? `${source.sourceEventId}:reaction:${opsSha256(source.reaction)}`
            : claim.kind === "SOURCE_LINKED"
                ? `${source.sourceEventId}:${opsSha256Utf8(source.excerpt)}:${source.highlightStart}:${source.highlightEnd}`
                : source.sourceEventId);
    if (new Set(proofKeys).size !== proofKeys.length) {
        context.addIssue({ code: "custom", path: ["evidenceSources"], message: "A claim cannot repeat the same proof occurrence." });
    }
    if (new Set(claim.workItemIds).size !== claim.workItemIds.length) {
        context.addIssue({ code: "custom", path: ["workItemIds"], message: "A claim may bind each Work item only once." });
    }
});
export function opsPublicEvidenceClaimsText(claims) {
    return claims.map((claim) => {
        const qualifier = claim.kind === "INFERENCE"
            ? "likely: "
            : claim.kind === "CONFLICT"
                ? "conflict: "
                : claim.kind === "UNRESOLVED"
                    ? "unresolved: "
                    : "";
        return `${claim.subject} — ${qualifier}${claim.text}`;
    }).join("\n");
}
const WebSourceSchema = z.object({
    id: z.string().min(1).max(160),
    title: z.string().min(1).max(500),
    url: SafeExternalHttpUrlSchema,
    summary: z.string().min(1).max(4_000)
}).strict();
// Content-free counters from the already-persisted live-core proof receipt.
// They deliberately carry no manifest/run/source identities or private text.
export const OpsPublicProofSummarySchema = z.object({
    schema: z.literal(OPS_PUBLIC_PROOF_SCHEMA),
    calls: z.int().nonnegative().max(12),
    rows: z.int().nonnegative().max(360_000),
    bytes: z.int().nonnegative().max(24_000_000)
}).strict();
// Inspection metadata, not business evidence. Keep it out of the canonical
// answer/Voice frame: a complete long-window inventory can exceed that frame.
export const OpsSourceCoverageUnavailableReasonSchema = z.enum([
    "SECRET_ENCRYPTED_MESSAGE",
    "UNKNOWN_UNREADABLE"
]);
export const OpsSourceCoverageItemSchema = z.object({
    sourceEventId: z.uuid(),
    textUnavailable: z.boolean(),
    recordKind: z.enum(["MESSAGE", "ALBUM_CONTAINER"]),
    // Content-free transport classification only. It never claims a decoder,
    // retained original, video meaning, or business relevance.
    unavailableReason: OpsSourceCoverageUnavailableReasonSchema.optional(),
    // Only unread ZIP members are listed. The parent source remains the exact
    // permission-bound navigation target; member path/reason is inspection data.
    archiveMembers: z.array(z.object({
        memberIndex: z.int().nonnegative(),
        path: z.string().min(1).max(4_096),
        status: z.enum(["NOT_READ", "DEFERRED"]),
        reason: z.string().min(1).max(120)
    }).strict()).min(1).max(8_000).optional(),
    attachment: z.object({
        mediaKind: z.enum(["DOCUMENT", "IMAGE", "AUDIO", "VIDEO"]),
        state: z.string().min(1).max(80).nullable(),
        statusCode: z.string().min(1).max(80).nullable(),
        declaredMimeType: z.string().min(3).max(160).nullable().optional()
    }).strict().optional()
}).strict().refine((source) => source.recordKind !== "ALBUM_CONTAINER"
    || (source.textUnavailable && source.attachment === undefined));
export const OPS_SOURCE_COVERAGE_PAGE_SIZE = 50;
export const OpsSourceCoveragePageSchema = z.object({
    sources: z.array(OpsSourceCoverageItemSchema).max(OPS_SOURCE_COVERAGE_PAGE_SIZE),
    totalCount: z.int().nonnegative(),
    nextOffset: z.int().nonnegative().nullable()
}).strict().refine((page) => page.sources.length <= page.totalCount
    && new Set(page.sources.map((source) => source.sourceEventId)).size === page.sources.length
    && (page.nextOffset === null || page.nextOffset < page.totalCount));
// WHY [BOL-J1-SOURCE-AVAILABILITY-DISCLOSURE-001]: the exact captured/no-text
// count is a scoped server read fact, not a message claim. The old suffix also
// said every such record's content and relevance were unknown, but the same
// receipt can classify protocol-only traffic and resolve a REVOKE target. New
// turns therefore state only the count. Keep the exact old renderer readable
// below so persisted answer bytes and hashes continue to validate unchanged.
export const OpsAnswerSourceAvailabilitySchema = z.object({
    capturedMessageCount: z.int().positive(),
    unavailableMessageCount: z.int().nonnegative(),
    unavailableAttachmentCount: z.int().positive().optional(),
    partialArchiveCount: z.int().positive().optional()
}).strict().refine((value) => value.capturedMessageCount >= value.unavailableMessageCount
    && value.capturedMessageCount >= (value.unavailableAttachmentCount ?? 0)
    && (value.partialArchiveCount ?? 0) <= (value.unavailableAttachmentCount ?? 0)
    && (value.unavailableMessageCount > 0 || (value.unavailableAttachmentCount ?? 0) > 0));
export function opsAnswerSourceAvailabilityDisclosure(value) {
    const counts = OpsAnswerSourceAvailabilitySchema.parse(value);
    // WHY [BOL-J1-ATTACHMENT-DISCLOSURE-001]: filenames can remain readable
    // while file bodies are unavailable. Keep that server receipt fact outside
    // business proof, and preserve the exact historical suffix when absent.
    const records = counts.unavailableMessageCount > 0
        ? `${counts.unavailableMessageCount} of ${counts.capturedMessageCount} captured WhatsApp records contained no readable message text.`
        : "";
    const files = counts.unavailableAttachmentCount
        ? counts.partialArchiveCount
            ? `Contents of ${counts.unavailableAttachmentCount} attached ${counts.unavailableAttachmentCount === 1 ? "file" : "files"} were unavailable or only partly readable. File names or captions are not the file contents.`
            : `Contents of ${counts.unavailableAttachmentCount} attached ${counts.unavailableAttachmentCount === 1 ? "file" : "files"} were unavailable. File names or captions are not the file contents.`
        : "";
    return [records, files].filter(Boolean).join(" ");
}
function opsAnswerLegacySourceAvailabilityDisclosure(value) {
    const counts = OpsAnswerSourceAvailabilitySchema.parse(value);
    const records = counts.unavailableMessageCount > 0
        ? `${counts.unavailableMessageCount} of ${counts.capturedMessageCount} captured WhatsApp records had no readable content. Their content and relevance remain unknown.`
        : "";
    const files = counts.unavailableAttachmentCount
        ? `Contents of ${counts.unavailableAttachmentCount} attached ${counts.unavailableAttachmentCount === 1 ? "file" : "files"} were unavailable. File names or captions are not the file contents.`
        : "";
    return [records, files].filter(Boolean).join(" ");
}
/** Exact authored-prefix extraction, never keyword or claim classification. */
export function opsAnswerBusinessText(response) {
    if (response.sourceAvailability === undefined)
        return response.text;
    const suffix = [
        opsAnswerSourceAvailabilityDisclosure(response.sourceAvailability),
        opsAnswerLegacySourceAvailabilityDisclosure(response.sourceAvailability)
    ].map((disclosure) => `\n\n${disclosure}`).find((candidate) => response.text.endsWith(candidate) && response.text.length !== candidate.length);
    if (!suffix) {
        throw new Error("OPS_PUBLIC_SOURCE_AVAILABILITY_TEXT_UNBOUND");
    }
    return response.text.slice(0, -suffix.length);
}
export const OpsAssistantPublicResponseSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("ANSWER"),
        text: z.string().min(1).max(20_000),
        format: z.enum(["CHAT", "REPORT"]).optional(),
        evidenceLabel: z.enum([
            "Current board", "Business rules", "Business sources", "WhatsApp sources",
            "WhatsApp route", "Web sources", "Google Workspace", "Google Meet transcript"
        ]),
        workItemIds: z.array(z.uuid()).max(200),
        proposalIds: z.array(z.uuid()).max(100).optional(),
        // WHY [BOL-PUBLIC-NATIVE-EVIDENCE-CAP-001]: native read authority permits
        // 320 cited sources, but the shared Text/Voice envelope rejected the 101st
        // card after a valid answer had already settled. Keep one aligned public
        // citation ceiling; claim verification still owns which sources are cited.
        // Falsifier: a <=320-source verified answer blanks at finalization, or an
        // unverified/out-of-scope source becomes public merely because this grew.
        evidenceSources: z.array(EvidenceSourceSchema).max(MAX_PUBLIC_EVIDENCE_SOURCES).optional(),
        // WHY [BOL-OPS-CLAIM-BOUND-PUBLIC-PROOF-001]: a global citation union let
        // unrelated source cards appear to support proof claims. New turns bind
        // each published proof claim to its own exact source words or Work record,
        // while primary-model answer prose remains a separate field in the same
        // hashed TurnResult. Claim count follows the model-selected business
        // outcomes, not unique-source cardinality: a valid uncapped Google result
        // must not fail only when this downstream finalizer sees claim 321.
        // Optionality exists solely so already-persisted v1 turns remain readable.
        // Falsifier: a proof claim loses/reassigns its evidence, answer prose is
        // mechanically rebuilt from cards, a valid claim is count-truncated, or a
        // legacy answer no longer reloads.
        evidenceClaims: z.array(OpsPublicEvidenceClaimSchema).min(1).optional(),
        webSources: z.array(WebSourceSchema).max(50).optional(),
        references: z.array(OpsAssistantReferenceSchema).max(20).optional(),
        artifacts: z.array(OpsAssistantArtifactSchema).max(4).optional(),
        workspaceReference: OpsAssistantWorkspaceReferenceSchema.optional(),
        proof: OpsPublicProofSummarySchema.optional(),
        sourceAvailability: OpsAnswerSourceAvailabilitySchema.optional(),
        operatingModelVersion: z.int().nonnegative()
    }).strict(),
    z.object({ kind: z.literal("CLARIFICATION"), text: z.string().min(1).max(20_000), operatingModelVersion: z.int().nonnegative() }).strict(),
    z.object({ kind: z.literal("FAILURE"), text: z.string().min(1).max(20_000), operatingModelVersion: z.int().nonnegative() }).strict(),
    z.object({ kind: z.literal("IMAGE_JOB"), text: z.string().min(1).max(20_000), job: OpsImageJobProjectionSchema, operatingModelVersion: z.int().nonnegative() }).strict(),
    z.object({
        kind: z.literal("MUTATION_PREVIEW"),
        text: z.string().min(1).max(20_000),
        operatingModelVersion: z.int().nonnegative(),
        preview: z.object({
            title: z.string().min(1).max(500),
            changes: z.array(z.object({
                label: z.string().min(1).max(240),
                current: z.string().max(4_000),
                next: z.string().max(4_000)
            }).strict()).max(100),
            command: OpsAssistantPublicMutationCommandSchema
        }).strict()
    }).strict()
]).superRefine((response, context) => {
    if (response.kind !== "ANSWER")
        return;
    let businessText;
    try {
        businessText = opsAnswerBusinessText(response);
    }
    catch {
        context.addIssue({ code: "custom", path: ["sourceAvailability"], message: "Source availability must bind its exact disclosure." });
        return;
    }
    if (response.sourceAvailability && (!response.evidenceClaims?.length
        || response.evidenceClaims.some((claim) => claim.answerSpan === undefined)
        || response.proposalIds?.length || response.workItemIds.length > 0)) {
        context.addIssue({ code: "custom", path: ["sourceAvailability"], message: "Source availability requires a span-bound read-only answer." });
    }
    // WHY [BOL-OPS-PUBLIC-SOURCE-CHANNEL-001]: MEETING/GOOGLE_MEET and WhatsApp
    // voice rows share VOICE_TRANSCRIPT. Letting presentation kind stand in for
    // channel identity gave retained Meet proof WhatsApp controls and references.
    // New sources carry their adapter channel and only WHATSAPP may advertise
    // OPEN_MESSAGE; the missing-channel case remains solely for persisted v1
    // compatibility. Falsifier: a known non-WhatsApp channel validates with
    // OPEN_MESSAGE, a Meet-labeled response loses its explicit Meet channel, a
    // Meet transcript validates as a file/text source, or an old unrelated
    // channel-less turn can no longer reload.
    (response.evidenceSources ?? []).forEach((source, sourceIndex) => {
        if (source.excerpt.length === 0 && source.reaction === undefined) {
            context.addIssue({
                code: "custom",
                path: ["evidenceSources", sourceIndex, "excerpt"],
                message: "Only typed reaction evidence may have an empty compatibility excerpt."
            });
        }
        if (source.reaction !== undefined
            && (source.channel !== "WHATSAPP" || source.sourceActions?.[0] !== "OPEN_MESSAGE")) {
            context.addIssue({
                code: "custom",
                path: ["evidenceSources", sourceIndex, "reaction"],
                message: "Reaction evidence must retain authorized WhatsApp source navigation."
            });
        }
        if (response.evidenceLabel === "Google Meet transcript"
            && source.channel !== "MEETING"
            && source.channel !== "GOOGLE_MEET") {
            context.addIssue({
                code: "custom",
                path: ["evidenceSources", sourceIndex, "channel"],
                message: "Meet-labeled proof must keep its explicit meeting channel."
            });
        }
        if (source.sourceActions !== undefined
            && ((source.channel !== undefined && source.channel !== "WHATSAPP")
                || source.kind === "FILE")) {
            context.addIssue({
                code: "custom",
                path: ["evidenceSources", sourceIndex, "sourceActions"],
                message: "Only an explicit WhatsApp source may open a message."
            });
        }
        if ((source.channel === "MEETING" || source.channel === "GOOGLE_MEET")
            && source.kind !== "VOICE_TRANSCRIPT") {
            context.addIssue({
                code: "custom",
                path: ["evidenceSources", sourceIndex, "kind"],
                message: "Meeting evidence must remain a transcript source."
            });
        }
        if ((source.channel === "OPS_UPLOAD" && source.kind !== "FILE")
            || (source.kind === "FILE" && source.channel !== undefined && source.channel !== "OPS_UPLOAD")) {
            context.addIssue({
                code: "custom",
                path: ["evidenceSources", sourceIndex, "channel"],
                message: "Retained file proof must keep its upload channel identity."
            });
        }
    });
    if (response.evidenceClaims === undefined)
        return;
    // WHY [BOL-OPS-MODEL-ANSWER-SEPARATE-PROOF-002]: a real primary J1 returned
    // five factual summary lines beside only two unrelated proof claims. Keep
    // Gemini's clean answer bytes separate from hidden proof metadata, but require
    // every meaningful business-answer byte on new span-aware turns to be the
    // exact text of one ordered proven claim. The only separate suffix is the
    // closed server availability disclosure validated above. The provider authors each claim once;
    // the app may join those exact ordered bytes with neutral separators before
    // hashing, then validates occurrence identity without rewriting meaning.
    // Claims with
    // no spans remain readable solely for persisted v1 compatibility.
    // Falsifier: a new business factual word exists outside a claim span, proof metadata
    // enters Text/Voice, claim order changes, or a legacy answer stops reloading.
    const claimsWithAnswerSpans = response.evidenceClaims.filter((claim) => claim.answerSpan !== undefined);
    if (claimsWithAnswerSpans.length > 0) {
        const expectedSpans = bindOpsOrderedAnswerClaimSpans(businessText, response.evidenceClaims.map((claim) => claim.text));
        if (claimsWithAnswerSpans.length !== response.evidenceClaims.length
            || !expectedSpans
            || expectedSpans.some((span, index) => {
                const actual = response.evidenceClaims[index].answerSpan;
                return !actual
                    || actual.text !== span.text
                    || actual.start !== span.start
                    || actual.end !== span.end;
            })) {
            context.addIssue({
                code: "custom",
                path: ["evidenceClaims"],
                message: "Every meaningful answer byte must bind to one ordered public evidence claim."
            });
        }
    }
    const workItemIds = new Set(response.workItemIds);
    const compatibilitySources = [];
    const compatibilitySourceById = new Map();
    const sameSourceIdentity = (left, right
    // Deletion status annotates a selected occurrence; it is not sender/time/body
    // identity and therefore cannot make identical source bytes conflict.
    ) => left.sourceEventId === right.sourceEventId
        && left.conversationLabel === right.conversationLabel
        && left.senderLabel === right.senderLabel
        && left.occurredAt === right.occurredAt
        && left.kind === right.kind
        && left.channel === right.channel
        && JSON.stringify(left.reaction ?? null) === JSON.stringify(right.reaction ?? null)
        && JSON.stringify(left.sourceActions ?? null) === JSON.stringify(right.sourceActions ?? null)
        && JSON.stringify(left.contact ?? null) === JSON.stringify(right.contact ?? null);
    const sameSource = (left, right) => sameSourceIdentity(left, right) && left.excerpt === right.excerpt;
    response.evidenceClaims.forEach((claim, claimIndex) => {
        claim.workItemIds.forEach((workItemId, workIndex) => {
            if (!workItemIds.has(workItemId)) {
                context.addIssue({
                    code: "custom",
                    path: ["evidenceClaims", claimIndex, "workItemIds", workIndex],
                    message: "Claim Work evidence must remain in the answer compatibility projection."
                });
            }
        });
        claim.evidenceSources.forEach((source, sourceIndex) => {
            const { visual: _visual, reaction: _reaction, highlight: _highlight, highlightStart: _highlightStart, highlightEnd: _highlightEnd, ...compatibilitySourceBase } = source;
            const compatibilitySource = {
                ...compatibilitySourceBase,
                ...(source.reaction ? { reaction: source.reaction } : {})
            };
            const existing = compatibilitySourceById.get(source.sourceEventId);
            // WHY [BOL-PUBLIC-CLAIM-LOCAL-CROP-001]: one immutable long source can
            // directly prove distant claims that cannot share one contiguous <=8k
            // public excerpt. Each claim therefore keeps its own exact verified crop,
            // while immutable source identity/capabilities must remain identical and
            // the legacy flat union keeps the first ordered crop. Requiring equal
            // excerpt bytes here recreated the old source-wide hull and could erase
            // the entire answer. Falsifier: differing source identity is accepted,
            // the flat union is not the first ordered crop, a claim highlight leaves
            // its crop, or a distant valid claim cannot survive persistence/reload.
            if (existing && !sameSourceIdentity(existing, compatibilitySource)) {
                context.addIssue({
                    code: "custom",
                    path: ["evidenceClaims", claimIndex, "evidenceSources", sourceIndex],
                    message: "One source ID cannot change immutable source identity across claims."
                });
                return;
            }
            if (!existing) {
                compatibilitySourceById.set(source.sourceEventId, compatibilitySource);
                compatibilitySources.push(compatibilitySource);
            }
        });
    });
    const flatSources = response.evidenceSources ?? [];
    if (flatSources.length !== compatibilitySources.length
        || flatSources.some((source, index) => !sameSource(source, compatibilitySources[index]))) {
        context.addIssue({
            code: "custom",
            path: ["evidenceSources"],
            message: "Flat evidence sources must exactly equal the ordered union of claim-local proof."
        });
    }
});
export const OpsApprovalReceiptProjectionSchema = z.object({
    id: z.uuid(),
    proposalRevision: z.int().positive(),
    status: z.enum(["ISSUED", "CLAIMED", "CONSUMED", "REVOKED", "EXPIRED"])
}).strict();
export const OpsProposalEffectReceiptProjectionSchema = z.object({
    id: z.uuid(),
    proposalRevision: z.int().positive(),
    effectKind: z.enum(["BOARD_UPDATE", "NEW_WORK", "BUSINESS_RULE_UPDATE", "SEND_MESSAGE"]),
    status: z.enum(["PENDING", "PROCESSING", "DELIVERED", "FAILED", "CANCELLED"])
}).strict();
export const OpsPublicProposalProjectionSchema = z.object({
    id: z.uuid(),
    state: AgentProposalStateSchema,
    version: z.int().positive(),
    currentRevision: z.int().positive(),
    approvedRevision: z.int().positive().nullable(),
    receipt: OpsApprovalReceiptProjectionSchema.nullable(),
    // Optional keeps persisted older turns readable. Every newly read approved
    // revision—and a cancellation that revoked that exact current revision—
    // carries the executor job receipt, including Work effects that have no
    // provider invocation receipt of their own.
    effectReceipt: OpsProposalEffectReceiptProjectionSchema.optional(),
    // Optional preserves old public turns while new terminal rereads carry the
    // exact durable at-most-once provider receipt. In particular, invokedAt is
    // not inferred from the pre-call marker when that observation is absent.
    providerInvocation: AgentProviderInvocationSchema.optional()
}).strict().superRefine((proposal, context) => {
    if (proposal.approvedRevision !== null && proposal.approvedRevision > proposal.currentRevision) {
        context.addIssue({ code: "custom", path: ["approvedRevision"], message: "An approved revision cannot be newer than the proposal." });
    }
    if (proposal.receipt && proposal.receipt.proposalRevision !== proposal.approvedRevision) {
        context.addIssue({ code: "custom", path: ["receipt", "proposalRevision"], message: "The receipt must bind the approved proposal revision." });
    }
    const effectRevision = proposal.approvedRevision
        ?? (proposal.state === "CANCELLED" ? proposal.currentRevision : null);
    if (proposal.effectReceipt && proposal.effectReceipt.proposalRevision !== effectRevision) {
        context.addIssue({ code: "custom", path: ["effectReceipt", "proposalRevision"], message: "The effect receipt must bind the exact approved or cancelled proposal revision." });
    }
    const terminalEffectStatus = {
        SUCCEEDED: "DELIVERED",
        CANCELLED: "CANCELLED",
        FAILED: "FAILED",
        DELIVERY_UNCERTAIN: "CANCELLED"
    };
    const expectedEffectStatus = terminalEffectStatus[proposal.state];
    if (proposal.effectReceipt && expectedEffectStatus
        && proposal.effectReceipt.status !== expectedEffectStatus) {
        context.addIssue({
            code: "custom",
            path: ["effectReceipt", "status"],
            message: "The executor receipt status must match the terminal proposal state."
        });
    }
});
export const OpsPublicTurnResultSchema = z.object({
    schema: z.literal(OPS_PUBLIC_TURN_RESULT_SCHEMA),
    author: z.literal("LUNA"),
    response: OpsAssistantPublicResponseSchema,
    proposals: z.array(OpsPublicProposalProjectionSchema).max(100)
}).strict().superRefine((result, context) => {
    const responseIds = result.response.kind === "ANSWER" ? [...(result.response.proposalIds ?? [])] : [];
    const projectionIds = result.proposals.map(({ id }) => id);
    if (new Set(projectionIds).size !== projectionIds.length) {
        context.addIssue({ code: "custom", path: ["proposals"], message: "A turn result may project each proposal only once." });
    }
    if (responseIds.length !== projectionIds.length || responseIds.some((id, index) => id !== projectionIds[index])) {
        context.addIssue({ code: "custom", path: ["proposals"], message: "Proposal state must exactly follow the response proposal IDs." });
    }
});
export const OpsPublicTurnEnvelopeSchema = z.object({
    schema: z.literal(OPS_PUBLIC_TURN_SCHEMA),
    turnId: PublicIdentifierSchema,
    requestId: PublicIdentifierSchema,
    revision: z.int().positive(),
    releaseId: OpsSha256Schema,
    answerHash: OpsSha256Schema,
    resultHash: OpsSha256Schema,
    result: OpsPublicTurnResultSchema
}).strict().superRefine((envelope, context) => {
    try {
        if (envelope.answerHash !== opsSha256Utf8(envelope.result.response.text)) {
            context.addIssue({ code: "custom", path: ["answerHash"], message: "The answer hash does not match the exact visible answer bytes." });
        }
        if (envelope.resultHash !== opsPublicTurnResultHash(envelope.result)) {
            context.addIssue({ code: "custom", path: ["resultHash"], message: "The result hash does not match the public turn result." });
        }
    }
    catch {
        // Identity parsing is a hostile-input boundary. Invalid Unicode or any
        // non-canonical result must become an ordinary validation failure so every
        // reload/render consumer can fail closed without throwing.
        context.addIssue({ code: "custom", path: ["result"], message: "The public turn result cannot be canonically hashed." });
    }
});
export function createOpsPublicTurnEnvelope(input) {
    const result = OpsPublicTurnResultSchema.parse(input.result);
    return OpsPublicTurnEnvelopeSchema.parse({
        schema: OPS_PUBLIC_TURN_SCHEMA,
        turnId: input.turnId,
        requestId: input.requestId,
        revision: input.revision ?? 1,
        releaseId: input.releaseId,
        answerHash: opsSha256Utf8(result.response.text),
        resultHash: opsPublicTurnResultHash(result),
        result
    });
}
export function reviseOpsPublicTurnEnvelope(currentValue, expected, nextResultValue) {
    const current = OpsPublicTurnEnvelopeSchema.safeParse(currentValue);
    const nextResult = OpsPublicTurnResultSchema.safeParse(nextResultValue);
    if (!current.success || !nextResult.success
        || current.data.turnId !== expected.turnId
        || current.data.requestId !== expected.requestId
        || current.data.revision !== expected.revision
        || current.data.releaseId !== expected.releaseId
        || current.data.resultHash !== expected.resultHash)
        return null;
    return createOpsPublicTurnEnvelope({
        turnId: current.data.turnId,
        requestId: current.data.requestId,
        revision: current.data.revision + 1,
        releaseId: current.data.releaseId,
        result: nextResult.data
    });
}
export function decodeOpsPublicTurnEnvelope(value, expectedReleaseId) {
    const parsed = OpsPublicTurnEnvelopeSchema.safeParse(value);
    return parsed.success && parsed.data.releaseId === expectedReleaseId ? parsed.data : null;
}
export function opsPublicTurnResultHash(resultValue) {
    return opsSha256(OpsPublicTurnResultSchema.parse(resultValue));
}
export function opsPublicResponseMatches(left, right) {
    const leftResponse = OpsAssistantPublicResponseSchema.safeParse(left);
    const rightResponse = OpsAssistantPublicResponseSchema.safeParse(right);
    if (!leftResponse.success || !rightResponse.success)
        return false;
    try {
        return opsCanonicalJson(leftResponse.data) === opsCanonicalJson(rightResponse.data);
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=ops-public-turn.js.map